/**
 * @copyright Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from "react-redux";

import {
    switchUniverse,
    clearAllFilters,
    setToggleMap,
    setExploreByContact,
    replaceFilters, setAccuracyData
} from "./universeSlice";
import { getFiltersMetaData } from "../../common/metadata/filtersMetaDataSlice";
import { countUniverse, searchUniverse, resolveUniverseFromCubeNamespace, clearResults } from "./universeQuerySlice";

import ScreenLockOverlay from "../../common/components/ScreenLockOverlay";
import Explore from './Explore';

export default function ExploreContainer() {
    const dispatch = useDispatch();

    // --- Redux Selectors ---
    const universeState = useSelector(state => state.universe);
    const searchCriteriaState = useSelector(state => state.searchData);
    const universeQueryState = useSelector(state => state.universeQuery);

    // --- Derived State ---
    const currentUniverse = universeState.currentUniverse;
    const currentUniverseData = universeState.universes[currentUniverse];
    const exploreByContact = universeState.universes[currentUniverse]?.exploreByContact ?? false;
    const accuracyData = useSelector(state => universeState.universes[currentUniverse]?.accuracyData);

    const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';

    const currentResults = universeQueryState.results[currentUniverse];

    const companyTotals = currentResults?.[modeKey]?.totalCompanies || 0;
    const contactTotals = currentResults?.[modeKey]?.totalContacts || 0;

    const [noServiceSearch, setNoServiceSearch] = useState(false);
    const [canExport, setCanExport] = useState(false);

    const toggleMap = currentUniverseData?.toggleMap ?? true;
    const isLoading = universeQueryState.searchLoading || universeQueryState.countLoading;

    const handleClearAllFilter = () => {
        dispatch(clearAllFilters());
        dispatch(clearResults(currentUniverse));
    };

    const handleToggleMapChange = (value) => {
        dispatch(setToggleMap(value));
    };

    const handleSearch = () => {
        dispatch(countUniverse({
            universeKey: currentUniverse,
            isMapEnabled: toggleMap
        }));

        dispatch(searchUniverse({
            universeKey: currentUniverse
        }));
    };

    const handleToggleUniverse = (universeKey) => {
        dispatch(switchUniverse(universeKey));
    };

    // --- Saved Search Logic ---
    const handleOnSelectSearchCriteria = (search) => {
        const selectedSearch = _.find(searchCriteriaState.searchCriteria, s => s.id === search.id);
        const query = selectedSearch?.query;
        const filters = query?.filters?.value;
        const type = query?.type;

        // Recover the universe + mode the search was built in. The cube namespace
        // in the saved query's dimensions is the only reliable signal (type alone
        // can't tell BUSINESS from CONTRACTOR). Fall back to type-based defaults
        // for legacy searches whose namespace we don't recognize.
        const savedCubeNamespace = query?.requestQuery?.dimensions?.[0]?.split('.')?.[0];
        const resolved = resolveUniverseFromCubeNamespace(savedCubeNamespace);
        const newUniverse = resolved?.universeKey ?? 'BUSINESS';
        const newExploreByContact = resolved?.exploreByContact ?? (type === 'SEARCH_CONTACT');

        // Order matters: switchUniverse first so the reducers below (which act on
        // state.currentUniverse) target the universe we're loading into.
        dispatch(switchUniverse(newUniverse));
        dispatch(setExploreByContact(newExploreByContact));
        dispatch(replaceFilters(filters));

        dispatch(countUniverse({
            universeKey: newUniverse,
            isMapEnabled: toggleMap
        }));

        dispatch(searchUniverse({
            universeKey: newUniverse
        }));
    };

    const settingAccuracy = (v) => {
        dispatch(setAccuracyData(v));
    };

    // --- Effects ---
    // Narrow deps to the subscription fields this effect actually uses. Using
    // the whole currentUniverseData object re-fired the metadata fetches every
    // time a filter was added (entities live inside that object, so Immer gave
    // it a new reference on every setFilterValue / removeFilter).
    const productId = currentUniverseData?.subscription?.productId;
    const exploreEnabled = currentUniverseData?.subscription?.features?.explore?.enabled;
    const engageEnabled = currentUniverseData?.subscription?.features?.engage?.enabled;
    const hasSubscriptionFeatures = !!currentUniverseData?.subscription?.features;

    useEffect(() => {
        if (productId) {
            dispatch(getFiltersMetaData(productId));
        }

        if (hasSubscriptionFeatures) {
            setNoServiceSearch(!exploreEnabled);
            setCanExport(!!engageEnabled);
        } else {
            setNoServiceSearch(true);
        }
    }, [dispatch, productId, exploreEnabled, engageEnabled, hasSubscriptionFeatures]);

    // Auto-apply once on load, equivalent to the user hitting "APPLY FILTER"
    // without touching anything, so the subscription's restriction filters
    // (prepareFilters -> buildRestrictionFilters) are already reflected in the
    // map/list results instead of showing an unfiltered, score-only query
    // until the first manual Apply.
    const hasAutoAppliedRef = useRef(false);
    useEffect(() => {
        if (hasAutoAppliedRef.current || !currentUniverse || !hasSubscriptionFeatures) {
            return;
        }

        hasAutoAppliedRef.current = true;
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUniverse, hasSubscriptionFeatures]);

    return (
        <>
            {noServiceSearch && <ScreenLockOverlay />}
            <Explore
                // State passed down
                currentUniverse={currentUniverse}
                hasFilter={exploreByContact ? contactTotals > 0 : companyTotals > 0}
                totalCompanies={companyTotals}
                totalContacts={contactTotals}
                loading={isLoading}
                canExport={canExport}
                accuracyData={accuracyData}

                // Search Criteria (Saved Searches)
                searchCriteria={searchCriteriaState.searchCriteria}

                toggleMap={toggleMap}
                onToggleMapChange={handleToggleMapChange}

                // Actions passed down
                handleToggleUniverse={handleToggleUniverse}
                handleSearch={handleSearch}
                handleClearAllFilter={handleClearAllFilter}
                handleOnSelectSearchCriteria={handleOnSelectSearchCriteria}
                handleSettingAccuracy={settingAccuracy}
            />
        </>
    );
}
